import React from 'react';
import { Icon, Spin } from '../components/ui.jsx';
import { api, fmt, fmtD } from '../api.js';

const SAMPLE_QUESTIONS = [
  'Quanti cantieri abbiamo nel 2026?',
  'Quanto abbiamo speso per i freelance nel primo trimestre?',
  'Quali cantieri ha fatto Giambalvo?',
  'Qual e il cliente con piu cantieri?',
  'Quanto fatturato abbiamo nel 2026?',
  'Quali tecnici hanno lavorato a piu di 5 cantieri?',
  'Mostra i cantieri di aprile con revenue',
  'Qual e la saturazione media dei tecnici?',
];

export const AssistantPage = () => {
  const [question, setQuestion] = React.useState('');
  const [history, setHistory] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const bottomRef = React.useRef(null);

  React.useEffect(() => {
    bottomRef.current?.scrollIntoView({behavior:'smooth'});
  }, [history]);

  const ask = async (q) => {
    const text = q || question.trim();
    if (!text) return;
    setQuestion('');
    setLoading(true);
    const entry = {question: text, loading: true};
    setHistory(prev => [...prev, entry]);
    try {
      const r = await fetch('/api/ai/query', {
        method: 'POST',
        headers: {'Content-Type':'application/json'},
        body: JSON.stringify({question: text})
      });
      const data = await r.json();
      setHistory(prev => {
        const updated = [...prev];
        updated[updated.length-1] = {...data, loading: false, error: data.detail || data.error || null};
        return updated;
      });
    } catch(e) {
      setHistory(prev => {
        const updated = [...prev];
        updated[updated.length-1] = {question: text, loading: false, error: e.message, results:[]};
        return updated;
      });
    }
    setLoading(false);
  };

  const formatValue = (v) => {
    if (v === null || v === undefined) return '-';
    if (typeof v === 'number') return v % 1 === 0 ? v.toLocaleString('it-IT') : v.toFixed(2);
    return String(v);
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Assistente AI</div>
          <div className="page-title-sub">Fai domande sui dati aziendali in italiano</div>
        </div>
      </div>

      {/* Chat history */}
      <div style={{maxHeight:'calc(100vh - 280px)',overflowY:'auto',marginBottom:16}}>
        {!history.length && (
          <div style={{padding:'40px 20px',textAlign:'center'}}>
            <Icon name="message-circle" size={40} color="var(--n200)" style={{margin:'0 auto 16px'}}/>
            <div style={{fontSize:15,fontWeight:600,color:'var(--n600)',marginBottom:8}}>Chiedimi qualcosa</div>
            <div style={{fontSize:13,color:'var(--n400)',marginBottom:24,maxWidth:400,margin:'0 auto'}}>
              Scrivi una domanda in italiano sui cantieri, tecnici, clienti, costi.
              Genero la query SQL e ti restituisco il risultato.
            </div>
            <div style={{display:'flex',flexWrap:'wrap',gap:8,justifyContent:'center',maxWidth:600,margin:'0 auto'}}>
              {SAMPLE_QUESTIONS.map(q => (
                <button key={q} onClick={() => ask(q)}
                  style={{padding:'8px 14px',borderRadius:10,fontSize:12,
                    background:'var(--n0)',border:'1.5px solid var(--n150)',color:'var(--n600)',
                    cursor:'pointer',transition:'all .15s',textAlign:'left',maxWidth:280}}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor='var(--blue)';e.currentTarget.style.color='var(--blue)';}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--n150)';e.currentTarget.style.color='var(--n600)';}}>
                  <Icon name="message-circle" size={11} color="var(--n300)" style={{marginRight:6}}/>
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {history.map((entry, i) => (
          <div key={i} style={{marginBottom:20}}>
            {/* Question */}
            <div style={{display:'flex',gap:10,marginBottom:10}}>
              <div style={{width:28,height:28,borderRadius:'50%',background:'var(--blue)',
                display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                <Icon name="user" size={14} color="#fff"/>
              </div>
              <div style={{flex:1,padding:'8px 14px',background:'var(--blue-50)',
                border:'1px solid var(--blue-pale)',borderRadius:'4px 12px 12px 12px',
                fontSize:14,color:'var(--blue-dark)',fontWeight:500}}>
                {entry.question}
              </div>
            </div>

            {/* Answer */}
            <div style={{display:'flex',gap:10,marginLeft:38}}>
              <div style={{flex:1}}>
                {entry.loading && (
                  <div style={{padding:16,display:'flex',alignItems:'center',gap:8,color:'var(--n400)',fontSize:13}}>
                    <Spin/> Analizzo la domanda e genero la query...
                  </div>
                )}
                {entry.error && (
                  <div className="alert alert-danger" style={{marginBottom:8}}>
                    <Icon name="alert-circle" size={14}/> {entry.error}
                  </div>
                )}
                {!entry.loading && entry.sql && (
                  <div style={{marginBottom:8}}>
                    <details style={{fontSize:11,color:'var(--n400)'}}>
                      <summary style={{cursor:'pointer',userSelect:'none',marginBottom:4}}>
                        <Icon name="code" size={10} color="var(--n300)" style={{marginRight:4}}/>
                        SQL generato
                      </summary>
                      <pre style={{background:'var(--n50)',border:'1px solid var(--n200)',
                        borderRadius:8,padding:12,fontSize:11,overflowX:'auto',
                        whiteSpace:'pre-wrap',fontFamily:'monospace',color:'var(--n600)'}}>
                        {entry.sql}
                      </pre>
                    </details>
                  </div>
                )}
                {!entry.loading && entry.results && entry.results.length > 0 && (
                  <div className="card" style={{padding:0,marginBottom:0}}>
                    <table>
                      <thead>
                        <tr>
                          {Object.keys(entry.results[0]).map(k => (
                            <th key={k} style={{textTransform:'none',letterSpacing:0,fontSize:11}}>
                              {k.replace(/_/g,' ')}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {entry.results.slice(0,20).map((row,ri) => (
                          <tr key={ri}>
                            {Object.values(row).map((v,vi) => (
                              <td key={vi} style={{fontSize:13,fontWeight:vi===0?600:400}}>
                                {formatValue(v)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {entry.count > 20 && (
                      <div style={{padding:'8px 16px',fontSize:11,color:'var(--n400)',
                        borderTop:'1px solid var(--border-soft)'}}>
                        Mostrati 20 di {entry.count} risultati
                      </div>
                    )}
                  </div>
                )}
                {!entry.loading && entry.results && entry.results.length === 0 && !entry.error && (
                  <div style={{padding:12,fontSize:13,color:'var(--n400)'}}>Nessun risultato</div>
                )}
              </div>
            </div>
          </div>
        ))}
        <div ref={bottomRef}/>
      </div>

      {/* Input */}
      <div style={{position:'sticky',bottom:0,background:'var(--bg)',padding:'12px 0',
        borderTop:'1px solid var(--border-soft)'}}>
        <div style={{display:'flex',gap:8}}>
          <input className="input" value={question} disabled={loading}
            onChange={e => setQuestion(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && ask()}
            placeholder="Fai una domanda... (es. Quanti cantieri ha fatto Follesa nel 2026?)"
            style={{flex:1,fontSize:14,padding:'12px 16px'}}/>
          <button className="btn btn-primary" onClick={() => ask()} disabled={loading || !question.trim()}
            style={{padding:'12px 20px'}}>
            {loading ? <Spin/> : <Icon name="send" size={16} color="#fff"/>}
          </button>
        </div>
      </div>
    </div>
  );
};
